import { Button } from '../../components/Button';
import {
  formatTrialDate,
  hasTrialWindow,
  trialDaysRemainingLabel,
  trialStatusOf,
} from './organizationTrial';
import type { OrganizationDetail, TrialStatus } from '../../types/admin';
import { AdminPanel } from './AdminPage';
import './OrganizationTrial.css';

interface OrganizationTrialPanelProps {
  organization: OrganizationDetail;
  onSetDates: () => void;
  onConvert: () => void;
  onClear: () => void;
}

/** The sentence under the heading, per band. Plain about the fact that nothing is enforced. */
const SUMMARY: Record<TrialStatus, string> = {
  none: 'This organization is not on a trial.',
  active: 'This organization is on a trial.',
  expiring: 'This trial is close to its end date.',
  expired:
    'This trial has run out. Nothing was switched off: trials only drive notices, and pausing an organization is a separate action.',
  converted: 'This organization became a paying customer.',
};

/**
 * An organization's trial, on its own page: the window, where it sits, and the things a system
 * admin can do to it.
 *
 * **Hidden entirely when the API did not send a trial status.** Web and the API deploy on separate
 * workflows off the same push, so a new build can meet a server with no trial endpoints; offering
 * buttons that can only 404 is worse than not offering them for the minutes the two are out of
 * step. This mirrors how the invite control hides itself on the organization settings screen.
 */
export const OrganizationTrialPanel = ({
  organization,
  onSetDates,
  onConvert,
  onClear,
}: OrganizationTrialPanelProps) => {
  if (organization.trialStatus === undefined) return null;

  const status = trialStatusOf(organization);
  const startedOn = formatTrialDate(organization.trialStartedAt);
  const endsOn = formatTrialDate(organization.trialEndsAt);
  const convertedOn = formatTrialDate(organization.trialConvertedAt);
  // Shown while a trial is still running only. The API sends 0 for an expired one, and "Last day"
  // beside "Trial ended" reads like something is still to come.
  const daysLeft =
    status === 'active' || status === 'expiring'
      ? trialDaysRemainingLabel(organization.trialDaysRemaining)
      : null;

  const isConverted = status === 'converted';
  const hasWindow = hasTrialWindow(organization);

  /*
    `trialStatusOf` collapses a band this build has never heard of down to `none`, so that nothing
    invents a word for it. The summary has to account for that: "not on a trial" printed directly
    above two real dates is the panel contradicting itself, and it is what a future server-side
    band would produce here.
  */
  const summary =
    status === 'none' && hasWindow
      ? 'This organization has trial dates set.'
      : SUMMARY[status];

  return (
    // The wrapper only carries the test id (AdminPanel takes no data attributes); it draws no box.
    <AdminPanel data-testid="org-trial-panel"
      className="org-trial"
      title="Trial"
      actions={
        <>
          {/*
            Editing is refused once an organization has converted (the API answers 409), so the
            button is not offered there. Clearing is, because it is the only route back from a
            conversion recorded by mistake.
          */}
          {!isConverted && (
            <Button variant="secondary" onClick={onSetDates} data-testid="org-trial-set">
              {hasWindow ? 'Edit trial dates' : 'Start a trial'}
            </Button>
          )}
          {!isConverted && hasWindow && (
            <Button
              variant="secondary"
              onClick={onConvert}
              data-testid="org-trial-convert"
            >
              Mark as converted
            </Button>
          )}
          {(hasWindow || isConverted) && (
            <Button variant="secondary" onClick={onClear} data-testid="org-trial-clear">
              Clear trial
            </Button>
          )}
        </>
      }
    >
      <p className="org-trial__summary" data-testid="org-trial-summary">
        {summary}
      </p>

      {(hasWindow || isConverted) && (
        <dl className="org-trial__facts">
          <div className="org-trial__fact">
            <dt className="org-trial__fact-label">Started</dt>
            <dd className="org-trial__fact-value" data-testid="org-trial-started">
              {startedOn ?? 'Not set'}
            </dd>
          </div>
          <div className="org-trial__fact">
            <dt className="org-trial__fact-label">Ends</dt>
            <dd className="org-trial__fact-value" data-testid="org-trial-ends">
              {endsOn ?? 'Not set'}
            </dd>
          </div>
          {daysLeft && (
            <div className="org-trial__fact">
              <dt className="org-trial__fact-label">Remaining</dt>
              <dd className="org-trial__fact-value" data-testid="org-trial-remaining">
                {daysLeft}
              </dd>
            </div>
          )}
          {convertedOn && (
            <div className="org-trial__fact">
              <dt className="org-trial__fact-label">Converted</dt>
              <dd className="org-trial__fact-value" data-testid="org-trial-converted">
                {convertedOn}
              </dd>
            </div>
          )}
        </dl>
      )}

      {/*
        Said plainly on the page rather than only in a confirm dialog. Converting is a one-way
        door, and an admin deciding whether to press it deserves to know that before the dialog
        rather than as a warning inside it.
      */}
      {isConverted && (
        <p className="org-trial__note" data-testid="org-trial-undo-note">
          There is no undo for a conversion. To put this organization back on a trial you have to
          clear the trial and enter the dates again, and the old dates are not kept.
        </p>
      )}
    </AdminPanel>
  );
};
